import { ActionsButtonItem } from '../../../../ui/components/pure/actions-button/actions-button.types';
import { Send } from '../../../../ui/components/pure/icons/Send.icon';
import { Add } from '../../../../ui/components/pure/icons/Add.icon';
import { KnownDeliveryHubDtoType, KnownDeliveryState } from '@groundfloor-platform/mgmt-service-g-sdk';
import { makeDefineActions } from '../../../../ui/components/pure/actions-button/actions-button.helpers';
import { ActionCollect } from '../../../../ui/components/pure/icons/ActionCollect.icon';
import { ActionMarkRetrieved } from '../../../../ui/components/pure/icons/ActionMarkRetrieved.icon';
import { ActionCancel } from '../../../../ui/components/pure/icons/ActionCancel.icon';
import { ActionOpen } from '../../../../ui/components/pure/icons/ActionOpen.icon';
import { ActionDelete } from '../../../../ui/components/pure/icons/ActionDelete.icon';
import { LinkIcon } from '../../../../ui/components/pure/icons/Link.icon';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { Switch } from '../../../../ui/components/pure/icons/Switch.icon';
import { Delivery } from '../../models/delivery.model';
import { BookingFlowType } from '../../../../ui/enums/booking-flow-type.enum';

export enum DeliveryDetailsActions {
  RESEND_NOTIFICATION = 'RESEND_NOTIFICATION',
  RESEND_NOTIFICATION_SUCCESS = 'RESEND_NOTIFICATION_SUCCESS',
  MARK_COLLECTED = 'MARK_COLLECTED',
  MARK_COLLECTED_SUCCESS = 'MARK_COLLECTED_SUCCESS',
  MARK_COLLECTED_FAILED = 'MARK_COLLECTED_FAILED',
  MARK_RETRIEVED = 'MARK_RETRIEVED',
  MARK_RETRIEVED_SUCCESS = 'MARK_RETRIEVED_SUCCESS',
  MARK_RETRIEVED_FAILED = 'MARK_RETRIEVED_FAILED',
  MARK_CANCELLED = 'MARK_CANCELLED',
  MARK_CANCELLED_SUCCESS = 'MARK_CANCELLED_SUCCESS',
  MARK_CANCELLED_FAILED = 'MARK_CANCELLED_FAILED',
  ARCHIVE_DELIVERY = 'ARCHIVE_DELIVERY',
  OPEN_LOCKER = 'OPEN_LOCKER',
  OPEN_LOCKER_SUCCESS = 'OPEN_LOCKER_SUCCESS',
  OPEN_LINK_TO_AC_DELIVERY = 'OPEN_LINK_TO_AC_DELIVERY',
  ADD_NOTE = 'ADD_NOTE',
  ADD_NOTE_SUCCESS = 'ADD_NOTE_SUCCESS',
  SWITCH_SPACE = 'SWITCH_SPACE',
  SWITCH_SPACE_SUCCESS = 'SWITCH_SPACE_SUCCESS',
  FORCE_UPDATE = 'FORCE_UPDATE',
  FORCE_UPDATE_SUCCESS = 'FORCE_UPDATE_SUCCESS'
}

/**
 * A delivery must be older than this before its state can be forced. Kiosk events
 * can arrive late, and a delivery cleaned up before they land would be settled on
 * incomplete information. Kept in step with FORCE_UPDATE_MINIMUM_AGE_DAYS in
 * mgmt-service-g.
 */
export const FORCE_UPDATE_MINIMUM_AGE_DAYS = 3;

export function meetsForceUpdateMinimumAge(delivery: Delivery): boolean {
  return deliveryAgeInDays(delivery) >= FORCE_UPDATE_MINIMUM_AGE_DAYS;
}

export function deliveryAgeInDays(delivery: Delivery): number {
  return (Date.now() - new Date(delivery.createdTime).getTime()) / 86_400_000;
}

function makeDeliveryDetailsActions(projectID: string): ActionsButtonItem<DeliveryDetailsActions>[] {
  return [
    {
      title: 'Add note',
      Icon: Add,
      action: DeliveryDetailsActions.ADD_NOTE,
      permission: {
        action: Permissions.notes.create,
        projectID
      }
    },
    {
      title: 'Resend notification',
      Icon: Send,
      action: DeliveryDetailsActions.RESEND_NOTIFICATION,
      permission: {
        action: Permissions.deliveries.sendNotification,
        projectID
      }
    },
    {
      title: 'Switch space',
      Icon: Switch,
      action: DeliveryDetailsActions.SWITCH_SPACE,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Open Locker',
      Icon: ActionOpen,
      action: DeliveryDetailsActions.OPEN_LOCKER,
      permission: {
        action: Permissions.deliveryContainers.open,
        projectID
      }
    },
    {
      title: 'Mark collected',
      Icon: ActionCollect,
      action: DeliveryDetailsActions.MARK_COLLECTED,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Mark retrieved',
      Icon: ActionMarkRetrieved,
      action: DeliveryDetailsActions.MARK_RETRIEVED,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Mark cancelled',
      Icon: ActionCancel,
      action: DeliveryDetailsActions.MARK_CANCELLED,
      permission: {
        action: Permissions.deliveries.cancel,
        projectID
      }
    },
    {
      title: 'Force update',
      Icon: Switch,
      action: DeliveryDetailsActions.FORCE_UPDATE,
      permission: {
        action: Permissions.deliveries.forceUpdate,
        projectID
      },
      availableUiFeatures: ['internal-groundfloor']
    },
    {
      title: 'Archive delivery',
      Icon: ActionDelete,
      action: DeliveryDetailsActions.ARCHIVE_DELIVERY,
      permission: {
        action: Permissions.deliveries.delete,
        projectID
      },
      availableUiFeatures: ['internal-groundfloor']
    },
    {
      title: 'View in old portal',
      Icon: LinkIcon,
      action: DeliveryDetailsActions.OPEN_LINK_TO_AC_DELIVERY,
      permission: {
        action: Permissions.deliveries.get,
        projectID
      },
      availableUiFeatures: ['internal-groundfloor']
    }
  ];
}

function makeValidateAction(delivery: Delivery, bookingFlowType: BookingFlowType) {
  return function validateAction(action: DeliveryDetailsActions) {
    const isRetailFlow = bookingFlowType === BookingFlowType.RETAIL;
    const isExternalHub = delivery.deliveredAtHub?.type === KnownDeliveryHubDtoType.LockersHubExternal;
    const isLockersHub =
      delivery.deliveredAtHub?.type === KnownDeliveryHubDtoType.LockersHub ||
      delivery.deliveredAtHub?.type === KnownDeliveryHubDtoType.LockersHubExternal;

    switch (action) {
      case DeliveryDetailsActions.ADD_NOTE:
        return true;
      case DeliveryDetailsActions.RESEND_NOTIFICATION:
        switch (delivery.state) {
          case KnownDeliveryState.Delivered:
          case KnownDeliveryState.Overdue:
            return true;
          default:
            return false;
        }
      case DeliveryDetailsActions.OPEN_LOCKER:
        // Only allowed for internal lockers hubs
        if (!isLockersHub || isExternalHub) {
          return false;
        }
        switch (delivery.state) {
          case KnownDeliveryState.Delivered:
          case KnownDeliveryState.Overdue:
          case KnownDeliveryState.RetrievalRequired:
          case KnownDeliveryState.Retrieved:
          case KnownDeliveryState.Collected:
            return true;
          default:
            return false;
        }
      case DeliveryDetailsActions.MARK_COLLECTED:
        switch (delivery.state) {
          // Moving to a final state from an intermediate state is only allowed on internal hubs
          case KnownDeliveryState.Delivered:
          case KnownDeliveryState.Overdue:
            return !isExternalHub;
          case KnownDeliveryState.Retrieved:
          case KnownDeliveryState.Cancelled:
            return true;
          default:
            return false;
        }
      case DeliveryDetailsActions.MARK_CANCELLED:
        switch (delivery.state) {
          // Moving to a final state from an intermediate state is only allowed on internal hubs
          case KnownDeliveryState.Delivered:
          case KnownDeliveryState.Overdue:
            return !isExternalHub;
          case KnownDeliveryState.Retrieved:
          case KnownDeliveryState.Collected:
            return true;
          default:
            return false;
        }
      case DeliveryDetailsActions.MARK_RETRIEVED:
        // Hide mark retrieved action for inventory flow projects
        if (!isRetailFlow) {
          return false;
        }
        switch (delivery.state) {
          // Moving to a final state from an intermediate state is only allowed on internal hubs
          case KnownDeliveryState.Delivered:
          case KnownDeliveryState.Overdue:
            return !isExternalHub;
          case KnownDeliveryState.Cancelled:
          case KnownDeliveryState.Collected:
            return true;
          default:
            return false;
        }
      case DeliveryDetailsActions.FORCE_UPDATE:
        // Shown for any in-progress delivery regardless of age. A delivery that is
        // too young opens the modal and is told why it cannot be forced yet, which
        // is clearer than the action being absent.
        return delivery.state === KnownDeliveryState.InProgress;
      case DeliveryDetailsActions.ARCHIVE_DELIVERY:
        switch (delivery.state) {
          case KnownDeliveryState.Abandoned:
          case KnownDeliveryState.Retrieved:
          case KnownDeliveryState.Cancelled:
          case KnownDeliveryState.Collected:
            return delivery.migratedFromAcDeliveryId === undefined;
          default:
            return false;
        }
      case DeliveryDetailsActions.OPEN_LINK_TO_AC_DELIVERY:
        return delivery.migratedFromAcDeliveryId !== undefined;
      case DeliveryDetailsActions.SWITCH_SPACE:
        return (
          delivery.deliveredInto?.hub.type === KnownDeliveryHubDtoType.KiosklessSpacesHub &&
          [KnownDeliveryState.Delivered, KnownDeliveryState.Overdue].includes(delivery.state as KnownDeliveryState)
        );
      default:
        return false;
    }
  };
}

export function makeDefineDeliveryDetailsActions(
  delivery: Delivery,
  projectID: string,
  bookingFlowType: BookingFlowType
) {
  return makeDefineActions(
    makeDeliveryDetailsActions(projectID),
    undefined,
    makeValidateAction(delivery, bookingFlowType)
  );
}
